import React, { useState, useEffect, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { Link } from '@reach/router';
import { Menu } from 'styled-icons/boxicons-regular/Menu';
import { UserCircle } from 'styled-icons/boxicons-regular/UserCircle';
import { LogOut } from 'styled-icons/boxicons-regular/LogOut';
import { Music } from 'styled-icons/boxicons-regular/Music';
import { useDispatch } from 'react-redux';
import { logoutUser } from 'state/user/UserActions';
import { Waves } from 'styled-icons/typicons/Waves';
import { Users } from 'styled-icons/fa-solid/Users';
import { UserShield } from 'styled-icons/fa-solid/UserShield';
import { Close } from 'styled-icons/evil/Close';
import useViewport from 'hooks/useViewport';
import { theme } from 'styles/ThemeProvider';
import { AdminViewModeContext } from 'state/AdminViewModeContext';

// DropdownContent visibility will be determined by css:hover on desktop and by toggling of open state on mobile/tablet
const NavDropdown: React.FC = (): JSX.Element => {
   const dispatch = useDispatch();
   const [open, setOpen] = useState(false);
   const [{ width: windowWidth }] = useViewport();
   const { setViewingMode } = useContext(AdminViewModeContext);

   const toggleDropdown = useCallback(() => {
      setOpen(!open);
   }, [open, setOpen]);

   useEffect(() => {
      // This useEffect allows the dropdown to be closed
      // when the user clicks anywhere outside of the dropdown itself
      const eventHandler = () => {
         setOpen(false);
         document.removeEventListener('click', eventHandler, false);
      };

      if (open) {
         document.addEventListener('click', eventHandler, false);
      }
   }, [open]);

   return (
      <Dropdown>
         <Button onClick={toggleDropdown} id="user-icon">
            {!open ? (
               <Menu size={30} fontWeight={300} color={theme.primaryBlueDark} />
            ) : windowWidth > 768 ? (
               <Close size={30} fontWeight={500} color={theme.primaryBlueDark} />
            ) : (
               <Menu size={30} fontWeight={300} color={theme.primaryBlueDark} />
            )}
         </Button>
         <Pointer />
         <DropdownContent visible={open} id="nav-dropdown-content">
            <ContentItem>
               <ContentButton>
                  <ContentLink to="/profile">
                     Profile <UserCircle size={20} fontWeight={300} color={theme.primaryBlueDark} />
                  </ContentLink>
               </ContentButton>
            </ContentItem>
            <ContentItem>
               <ContentButton>
                  <ContentLink to="/waveforms">
                     Waveforms <Waves size={25} fontWeight={300} color={theme.primaryBlueDark} />
                  </ContentLink>
               </ContentButton>
            </ContentItem>
            <ContentItem>
               <ContentButton>
                  <ContentLink to="/sensations">
                     Sensations <Music size={20} fontWeight={300} color={theme.primaryBlueDark} />
                  </ContentLink>
               </ContentButton>
            </ContentItem>
            <ContentItem>
               <ContentButton>
                  <ContentLink to="/customers">
                     Customers <Users size={20} fontWeight={300} color={theme.primaryBlueDark} />
                  </ContentLink>
               </ContentButton>
            </ContentItem>
            <ContentItem>
               <ContentButton>
                  <ContentLink to="/admins">
                     Admins <UserShield size={20} fontWeight={300} color={theme.primaryBlueDark} />
                  </ContentLink>
               </ContentButton>
            </ContentItem>
            <ContentItem>
               <ContentButton onClick={() => setViewingMode('customer')}>View as customer</ContentButton>
            </ContentItem>
            <ContentItem>
               <ContentButton onClick={() => dispatch(logoutUser())}>
                  Logout <LogOut size={20} fontWeight={300} color={theme.primaryBlueDark} />
               </ContentButton>
            </ContentItem>
         </DropdownContent>
      </Dropdown>
   );
};

export default NavDropdown;

const DropdownContent = styled.div`
   display: none;
   position: absolute;
   right: 0;
   border-radius: 7px;
   background-color: #f9f9f9;
   min-width: 160px;
   border: 1px solid ${({ theme }) => theme.primaryGrey};
   box-shadow: ${({ theme }) => theme.boxShadow};
   z-index: 100;
   top: 40px;

   @media only screen and (max-width: 600px) {
      display: ${({ visible }: { visible: boolean }) => (visible ? 'block' : 'none')};
      width: 40%;
   }
`;

const ContentItem = styled.div`
   height: 40px;
   padding: 6px 7px;
   display: flex;
   flex-direction: row;
   justify-content: flex-start;
   align-items: center;

   &:hover {
      background-color: #c7c7c7;
      cursor: pointer;
   }
`;

const ContentButton = styled.button.attrs({ type: 'button' })`
   border: none;
   background-color: transparent;
   font-size: 1em;
   cursor: pointer;
   width: 100%;

   &:hover {
      color: ${({ theme }) => theme.secondaryBlue};
   }
`;

const ContentLink = styled(Link)`
   text-decoration: none;
   color: inherit;
   display: flex;
   flex-direction: row;
   justify-content: space-between;
   align-items: center;
`;

const Button = styled.button.attrs({ type: 'button' })`
   background: transparent;
   border: none;
   color: ${({ theme }) => theme.primaryWhite};
   font-size: 1.2em;
   display: flex;
   flex-direction: row;
   justify-content: space-between;
   align-content: center;
   &:hover {
      cursor: pointer;
   }
`;
const Pointer = styled.div`
   width: 0;
   height: 0;
   display: none;
   position: absolute;
   border-left: 10px solid transparent;
   border-right: 10px solid transparent;
   left: 14px;
   border-bottom: 10px solid #303f5e;
`;

const Dropdown = styled.div`
   position: absolute;
   background: transparent;
   width: auto;
   text-align: center;
   display: none;
   left: 0;
   margin-left: 15px;
   &:hover ${DropdownContent} {
      display: block;
   }

   &:hover ${Pointer} {
      display: block;
   }

   @media only screen and (max-width: 768px) {
      right: 10px;
      left: initial;
      display: block;
   }
`;
